import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, MessageSquare, Bell, Sun, MapPin } from "lucide-react";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Phone number formatter
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  const phoneNumberLength = phoneNumber.length;
  
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

export default function TechDialog({ open, onOpenChange, onSuccess, editTech = null }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    role: "Tech",
    notify_sms: true,
    notify_email: true,
    notify_new_estimates: false,
    daily_sms_reminder: true,
    color: "",
    is_contractor: false,
    capabilities: []
  });
  const [loading, setLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const isEditing = !!editTech;

  // Google Places Autocomplete plumbing (same pattern as CustomerDialog).
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Populate form when editing
  useEffect(() => {
    if (editTech) {
      setFormData({
        name: editTech.name || "",
        email: editTech.email || "",
        phone: editTech.phone || "",
        address: editTech.address || "",
        role: editTech.role || "Tech",
        notify_sms: editTech.notify_sms !== false,
        notify_email: editTech.notify_email !== false,
        notify_new_estimates: editTech.notify_new_estimates || false,
        // Default ON when the field is undefined (existing techs pre-feature)
        daily_sms_reminder: editTech.daily_sms_reminder !== false,
        color: editTech.color || "",
        is_contractor: !!editTech.is_contractor,
        capabilities: Array.isArray(editTech.capabilities) ? editTech.capabilities : []
      });
    } else {
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        role: "Tech",
        notify_sms: true,
        notify_email: true,
        notify_new_estimates: false,
        daily_sms_reminder: true,
        color: "",
        is_contractor: false
      });
    }
  }, [editTech, open]);

  // Wire up Google Places Autocomplete on the address input whenever the
  // dialog opens. Mirrors CustomerDialog.js — waits for the Places API to
  // be ready, then attaches an autocomplete instance and updates the form
  // on place_changed.
  useEffect(() => {
    if (!open) return;

    const timeoutId = setTimeout(() => {
      if (!addressInputRef.current) return;

      const initAutocomplete = () => {
        if (!window.google?.maps?.places) return false;
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
          autocompleteRef.current = null;
        }
        try {
          autocompleteRef.current = new window.google.maps.places.Autocomplete(
            addressInputRef.current,
            {
              componentRestrictions: { country: "us" },
              fields: ["formatted_address", "address_components"],
              types: ["address"],
            }
          );
          autocompleteRef.current.addListener("place_changed", () => {
            const place = autocompleteRef.current.getPlace();
            if (place?.formatted_address) {
              setFormData(prev => ({ ...prev, address: place.formatted_address }));
            }
          });
          return true;
        } catch (_err) {
          return false;
        }
      };

      if (window.googleMapsReady || window.google?.maps?.places) {
        initAutocomplete();
      } else {
        const originalCallback = window.onGoogleMapsReady;
        window.onGoogleMapsReady = () => {
          if (originalCallback) originalCallback();
          initAutocomplete();
        };
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [open]);

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const payload = {
        ...formData,
        phone: formData.phone || null,
        // Empty color = "auto" (deterministic fallback). Send explicit null
        // so the backend can distinguish "unset" from any real hex.
        color: formData.color || null,
      };

      if (isEditing) {
        await axios.put(`${API}/techs/${editTech.tech_id}`, payload, { headers });
        toast.success("Tech updated successfully");
      } else {
        await axios.post(`${API}/techs`, payload, { headers });
        toast.success("Tech added successfully");
      }
      
      setFormData({
        name: "",
        email: "",
        phone: "",
        role: "Tech",
        notify_sms: true,
        notify_email: true,
        notify_new_estimates: false,
        daily_sms_reminder: true,
        color: ""
      });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'add'} tech`);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestReminder = async () => {
    if (!editTech?.tech_id) return;
    setSendingTest(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(
        `${API}/techs/${editTech.tech_id}/send-test-daily-reminder`,
        {},
        { headers }
      );
      toast.success(`Test reminder sent (${data.job_count} job${data.job_count === 1 ? "" : "s"} today)`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === "no_jobs_today") {
        toast.info("No jobs scheduled for this tech today — nothing to send.");
      } else {
        toast.error(detail || "Failed to send test reminder");
      }
    } finally {
      setSendingTest(false);
    }
  };

  // Fetch today's SMS preview whenever the dialog opens for an existing
  // tech AND their reminder is enabled. Read-only — no Twilio call.
  useEffect(() => {
    let cancelled = false;
    async function loadPreview() {
      if (!open || !isEditing || !editTech?.tech_id || !formData.daily_sms_reminder) {
        setPreview(null);
        return;
      }
      setPreviewLoading(true);
      try {
        const token = localStorage.getItem('session_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.get(
          `${API}/techs/${editTech.tech_id}/daily-reminder-preview`,
          { headers }
        );
        if (!cancelled) setPreview(data);
      } catch (_err) {
        if (!cancelled) setPreview({ ok: false, job_count: 0, body: "" });
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }
    loadPreview();
    return () => { cancelled = true; };
  }, [open, isEditing, editTech?.tech_id, formData.daily_sms_reminder]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="tech-dialog">
        <DialogHeader>
          <DialogTitle className="font-barlow text-2xl uppercase">
            {isEditing ? "Edit Tech" : "Add New Tech"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEditing ? "Edit technician details" : "Add a new screen technician to the system"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name" className="font-inter font-semibold text-zinc-700">
              Name *
            </Label>
            <Input
              id="name"
              data-testid="tech-name-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="h-12 mt-1"
            />
          </div>

          <div>
            <Label htmlFor="email" className="font-inter font-semibold text-zinc-700">
              Email *
            </Label>
            <Input
              id="email"
              data-testid="tech-email-input"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="h-12 mt-1"
            />
            <p className="text-xs text-zinc-500 mt-1">Must match their Google account email</p>
          </div>

          <div>
            <Label htmlFor="phone" className="font-inter font-semibold text-zinc-700">
              Phone Number
            </Label>
            <Input
              id="phone"
              data-testid="tech-phone-input"
              type="tel"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="(555) 123-4567"
              className="h-12 mt-1"
            />
            <p className="text-xs text-zinc-500 mt-1">Required for SMS notifications</p>
          </div>

          <div>
            <Label htmlFor="tech-address" className="font-inter font-semibold text-zinc-700 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Address
            </Label>
            <Input
              id="tech-address"
              data-testid="tech-address-input"
              ref={addressInputRef}
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="123 Main St, Murrieta, CA 92562"
              className="h-12 mt-1"
              autoComplete="off"
            />
            <p className="text-xs text-zinc-500 mt-1">For material drop-offs or customer drop-offs</p>
          </div>

          <div>
            <Label htmlFor="role" className="font-inter font-semibold text-zinc-700">
              Role *
            </Label>
            <select
              id="role"
              data-testid="tech-role-select"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
              className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white mt-1"
            >
              <option value="Tech">Tech</option>
              <option value="Admin">Admin</option>
            </select>
          </div>

          {/* Notification Settings */}
          <div className="border-t pt-4">
            <Label className="font-inter font-semibold text-zinc-700 flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4" />
              Notification Settings
            </Label>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                <input
                  type="checkbox"
                  checked={formData.notify_sms}
                  onChange={(e) => setFormData({ ...formData, notify_sms: e.target.checked })}
                  className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                />
                <MessageSquare className="w-5 h-5 text-zinc-500" />
                <div className="flex-1">
                  <div className="font-medium text-sm">SMS Notifications</div>
                  <div className="text-xs text-zinc-500">Receive text messages for new appointments</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                <input
                  type="checkbox"
                  checked={formData.notify_email}
                  onChange={(e) => setFormData({ ...formData, notify_email: e.target.checked })}
                  className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                />
                <Mail className="w-5 h-5 text-zinc-500" />
                <div className="flex-1">
                  <div className="font-medium text-sm">Email Notifications</div>
                  <div className="text-xs text-zinc-500">Receive emails for new appointments</div>
                </div>
              </label>

              {/* Admin-only: New Estimate Notifications */}
              {formData.role === "Admin" && (
                <label className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100">
                  <input
                    type="checkbox"
                    checked={formData.notify_new_estimates}
                    onChange={(e) => setFormData({ ...formData, notify_new_estimates: e.target.checked })}
                    className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                  />
                  <Bell className="w-5 h-5 text-amber-600" />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-amber-800">New Estimate Alerts</div>
                    <div className="text-xs text-amber-700">Get notified when customers request estimates (so you can follow up)</div>
                  </div>
                </label>
              )}

              {/* Daily 8:00 AM PST schedule reminder */}
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50" data-testid="tech-daily-reminder-row">
                <input
                  type="checkbox"
                  data-testid="tech-daily-reminder-checkbox"
                  checked={formData.daily_sms_reminder}
                  onChange={(e) => setFormData({ ...formData, daily_sms_reminder: e.target.checked })}
                  className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                />
                <Sun className="w-5 h-5 text-zinc-500" />
                <div className="flex-1">
                  <div className="font-medium text-sm">Daily Schedule Reminder</div>
                  <div className="text-xs text-zinc-500">8:00 AM PST text with today&apos;s appointments (skipped if no jobs).</div>
                </div>
              </label>

              {isEditing && formData.daily_sms_reminder && (
                <div
                  className="rounded-lg border border-zinc-200 bg-zinc-50 p-3"
                  data-testid="tech-daily-reminder-preview"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">
                      Today&apos;s SMS Preview
                    </div>
                    {preview?.date_pst && (
                      <div className="text-[10px] text-zinc-500 font-mono">
                        {preview.date_pst} PST
                      </div>
                    )}
                  </div>
                  {previewLoading ? (
                    <div className="text-xs text-zinc-500 italic">Loading…</div>
                  ) : preview && preview.job_count > 0 ? (
                    <pre className="whitespace-pre-wrap text-[12px] leading-snug text-zinc-800 font-mono bg-white rounded border border-zinc-200 p-2 max-h-56 overflow-y-auto">
{preview.body}
                    </pre>
                  ) : (
                    <div className="text-xs text-zinc-500 italic">
                      No jobs scheduled for this tech today — no SMS will be sent.
                    </div>
                  )}
                </div>
              )}

              {/* --- Contractor block --- */}
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-zinc-200 bg-white cursor-pointer hover:border-orange-400 transition-colors">
                <input
                  type="checkbox"
                  data-testid="tech-is-contractor-checkbox"
                  checked={formData.is_contractor}
                  onChange={(e) => setFormData({ ...formData, is_contractor: e.target.checked })}
                  className="w-5 h-5 rounded border-zinc-300 text-orange-600 focus:ring-orange-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Contractor / Subcontractor</div>
                  <div className="text-xs text-zinc-500">
                    Materials + labor rolled into a flat payout entered when you mark the invoice paid.
                  </div>
                </div>
              </label>

              {/* --- Job capabilities --- */}
              <div className="p-3 rounded-lg border-2 border-zinc-200 bg-white">
                <div className="font-medium text-sm mb-1">Jobs this tech can do</div>
                <div className="text-xs text-zinc-500 mb-3">
                  Leave all unchecked to allow every job. When any box is checked, the booking flow will
                  hide this tech's slots for estimates that need a job they can't do.
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    ["window_rescreen", "Window rescreens"],
                    ["window_new", "New window screens"],
                    ["door_rescreen", "Screen door rescreens"],
                    ["door_new", "New screen doors"],
                    ["heavy_duty_door", "Heavy-duty door upgrade"],
                    ["patio_track", "Patio door / sliding track"],
                    ["swing_door", "Swinging screen doors"],
                  ].map(([key, label]) => {
                    const checked = (formData.capabilities || []).includes(key);
                    return (
                      <label key={key} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          data-testid={`tech-cap-${key}`}
                          checked={checked}
                          onChange={(e) => {
                            const cur = new Set(formData.capabilities || []);
                            if (e.target.checked) cur.add(key); else cur.delete(key);
                            setFormData({ ...formData, capabilities: Array.from(cur) });
                          }}
                          className="w-4 h-4 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {isEditing && formData.daily_sms_reminder && (
                <button
                  type="button"
                  onClick={handleSendTestReminder}
                  disabled={sendingTest || !formData.phone || !(preview && preview.job_count > 0)}
                  data-testid="tech-send-test-reminder-button"
                  className="w-full h-10 rounded-lg border-2 border-[#207024] text-[#207024] font-semibold hover:bg-[#207024] hover:text-white transition-colors disabled:opacity-50 text-sm"
                >
                  {sendingTest ? "Sending..." : "Send Test Reminder Now"}
                </button>
              )}
            </div>
          </div>

          {/* Calendar Color — visual override for the deterministic tech
              color used on Jobs calendar cards. Empty = auto (hash-based). */}
          <div className="border-t pt-4">
            <Label className="font-inter font-semibold text-zinc-700 mb-2 block">
              Calendar Color
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              {[
                "", "#207024", "#2563eb", "#c026d3", "#ea580c",
                "#0891b2", "#dc2626", "#65a30d", "#7c3aed", "#0d9488", "#b45309",
              ].map((swatch) => (
                <button
                  key={swatch || "auto"}
                  type="button"
                  onClick={() => setFormData({ ...formData, color: swatch })}
                  data-testid={`tech-color-${swatch || "auto"}`}
                  className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-transform ${
                    formData.color === swatch ? "scale-110 ring-2 ring-offset-1 ring-zinc-400" : ""
                  }`}
                  style={{
                    backgroundColor: swatch || "transparent",
                    borderColor: swatch ? swatch : "#d4d4d8",
                    color: "#ffffff",
                  }}
                  aria-label={swatch ? `Set color to ${swatch}` : "Auto (deterministic)"}
                >
                  {!swatch && <span className="text-[10px] font-bold text-zinc-500">AUTO</span>}
                </button>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              Overrides the auto-assigned color for this tech on Jobs calendar cards. Pick <span className="font-semibold">AUTO</span> to fall back to the deterministic hash-based color.
            </p>
          </div>

          {!isEditing && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800 font-inter">
                Note: The new tech will be able to log in using their Google account once added.
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                onOpenChange(false);
                setFormData({
                  name: "",
                  email: "",
                  phone: "",
                  address: "",
                  role: "Tech",
                  notify_sms: true,
                  notify_email: true,
                  notify_new_estimates: false,
                  daily_sms_reminder: true
                });
              }}
              data-testid="tech-cancel-button"
              className="h-12 flex-1 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 font-semibold hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="tech-submit-button"
              className="h-12 flex-1 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold disabled:opacity-50"
            >
              {loading ? (isEditing ? "Saving..." : "Adding...") : (isEditing ? "Save Changes" : "Add Tech")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
